// src/utils/fileVaultApiClient.js
import { getAuth } from "firebase/auth";

const API_HOST = process.env.REACT_APP_BACKEND_URL || "";
const baseUrl = `${API_HOST.replace(/\/+$/, "")}/vault`;

/* -------------------- auth + fetch helpers -------------------- */
async function getIdToken() {
  try {
    const user = getAuth()?.currentUser;
    if (!user) return "";
    return (await user.getIdToken(false)) || "";
  } catch {
    return "";
  }
}

async function authJson(method, path, body) {
  const idToken = await getIdToken();

  let res;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
  } catch (networkErr) {
    const err = new Error("Network error. Please check your connection.");
    err.cause = networkErr;
    err.status = 0;
    throw err;
  }

  let data = {};
  try {
    data = await res.json();
  } catch {
    // Non-JSON; leave as {}
  }

  if (!res.ok) {
    const msg =
      data?.error?.message ||
      data?.error ||
      data?.message ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    err.code = data?.code || data?.error?.code || "";
    throw err;
  }

  return data;
}


function isNoSuchKeyError(err) {
  const status = Number(err?.status || 0);
  const code = String(err?.code || err?.payload?.code || err?.payload?.error?.code || "");
  const msg = String(err?.message || err?.payload?.message || err?.payload?.error?.message || "");

  
  if (status === 409 && code === "gcs_object_missing") return true;

  
  if ((status === 404 || status === 409) && /no such (key|object)|not found/i.test(msg)) return true;

  return false;
}

async function retryOnNoSuchKey(fn, { attempts = 5, baseDelayMs = 250 } = {}) {
  let lastErr = null;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (!isNoSuchKeyError(e) || i === attempts - 1) throw e;
      const delay = baseDelayMs * Math.pow(2, i);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr || new Error("Request failed");
}


async function uploadToSignedUrl({ url, file, contentType }) {
  let res;
  try {
    res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": contentType || "application/octet-stream" },
      body: file,
    });
  } catch (networkErr) {
    const err = new Error("Network error during upload.");
    err.cause = networkErr;
    err.status = 0;
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const msg = text || `Upload failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return true;
}

/* -------------------- small helpers -------------------- */
function normParentId(parentId) {
  // Backend treats root as Firestore null; send explicit null in bodies.
  return parentId == null || parentId === "" ? null : parentId;
}

function buildResourceId(kind, nodeId) {
  return `${kind === "folder" ? "folders" : "files"}/${nodeId}`;
}

function withOwnerQuery(qs, owner) {
  if (!owner) return qs;
  return `${qs}${qs.includes("?") ? "&" : "?"}owner=${encodeURIComponent(
    String(owner).toLowerCase().trim()
  )}`;
}

/* ======================================================================= */
/*                                  API                                    */
/* ======================================================================= */
export const fileVaultApi = {
  // -------- listing (my files or inside someone else's shared folder) --------
  async listChildren({ parentId = null, owner } = {}) {
    let q = parentId ? `?parentId=${encodeURIComponent(parentId)}` : "?parentId=";
    q = withOwnerQuery(q, owner);
    return authJson("GET", `/nodes/children${q}`);
  },

  // -------- shared-with-me listing --------
  async listSharedWithMe({ limit = 200, owner } = {}) {
    let q = `?limit=${encodeURIComponent(limit)}`;
    q = withOwnerQuery(q, owner); // optional owner filter
    return authJson("GET", `/nodes/shared-with-me${q}`);
  },

  // -------- folders --------
  async createFolder({ name, parentId = null, owner } = {}) {
    return authJson("POST", "/nodes/folder", {
      name,
      parentId: normParentId(parentId),
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  // -------- files (metadata node first) --------
  async createFileNode({ name, size, contentType, parentId = null, owner } = {}) {
    return authJson("POST", "/nodes/file", {
      name,
      size,
      contentType: (contentType || "").toLowerCase(),
      parentId: normParentId(parentId),
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  // -------- fetch single node (supports owner override) --------
  async getNode({ nodeId, owner } = {}) {
    let q = `?nodeId=${encodeURIComponent(nodeId)}`;
    q = withOwnerQuery(q, owner);
    return authJson("GET", `/nodes/get${q}`);
  },

  // -------- signed URLs --------
  async getUploadUrl({ nodeId, contentType, size, owner } = {}) {
    const body = {
      nodeId,
      contentType: (contentType || "").toLowerCase(),
      ...(typeof size === "number" && size > 0 ? { size } : {}),
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    };
    // Upload-url usually exists immediately; keep as-is (no retry needed)
    return authJson("POST", "/transfer/upload-url", body);
  },

  async getDownloadUrl({ nodeId, owner } = {}) {
    const body = {
      nodeId,
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    };
   
    return retryOnNoSuchKey(() => authJson("POST", "/transfer/download-url", body), {
      attempts: 5,
      baseDelayMs: 250,
    });
  },

  async getPreviewUrl({ nodeId, owner } = {}) {
    const body = {
      nodeId,
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    };

    
    try {
      return await retryOnNoSuchKey(() => authJson("POST", "/transfer/preview-url", body), {
        attempts: 5,
        baseDelayMs: 250,
      });
    } catch (err) {
   
      if ([404, 415, 422, 501].includes(err?.status)) {
        
        return retryOnNoSuchKey(() => authJson("POST", "/transfer/download-url", body), {
          attempts: 5,
          baseDelayMs: 250,
        });
      }
      throw err;
    }
  },

  async commitUpload({ nodeId, owner } = {}) {
    return authJson("POST", "/transfer/commit", {
      nodeId,
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  // -------- File Vault -> Document Intelligence --------
  async analyzeFile({ nodeId, owner } = {}) {
    if (!nodeId) {
      const err = new Error("Missing nodeId");
      err.status = 400;
      err.code = "file_vault_node_id_required";
      throw err;
    }

    // Do not automatically retry this request. The backend may already have
    // resolved a Chat session and started Document Intelligence before a client
    // network failure becomes visible. Retrying here could create duplicate
    // paid sessions or duplicate processing attempts.
    return authJson("POST", "/analyze", {
      nodeId,
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  // -------- mutations --------
  async renameNode({ nodeId, name, owner } = {}) {
    return authJson("PATCH", `/nodes/${encodeURIComponent(nodeId)}`, {
      name,
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  async moveNode({ nodeId, parentId, owner } = {}) {
    return authJson("PATCH", `/nodes/${encodeURIComponent(nodeId)}`, {
      parentId: normParentId(parentId),
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  async deleteNode({ nodeId, owner } = {}) {
    // Backend accepts owner in body for DELETE as well
    return authJson("DELETE", `/nodes/${encodeURIComponent(nodeId)}`, {
      ...(owner ? { owner: String(owner).toLowerCase().trim() } : {}),
    });
  },

  /* ============================== SHARING ============================== */

  async putShare(payload) {
    // Back-compat: normalize simple invite into the new body
    if (payload && !payload.resourceId) {
      const {
        nodeId,
        kind = "file",
        email,
        role = "viewer",
        expiresInDays,
        allowDownload,
      } = payload;
      const resourceId = buildResourceId(kind, nodeId);
      const expiresAt =
        typeof expiresInDays === "number"
          ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
          : null;
      const people = [
        {
          email: String(email || "").trim().toLowerCase(),
          role,
          expiresAt,
          ...(typeof allowDownload === "boolean" ? { allowDownload } : {}),
        },
      ];
      return authJson("POST", "/share/put", { resourceId, people });
    }

    // Flexible body
    const { resourceId, people, link } = payload || {};
    if (!resourceId) throw new Error("Missing resourceId");

    const cleanedPeople =
      Array.isArray(people) && people.length
        ? people.map((p) => ({
            email: String(p.email || "").trim().toLowerCase(),
            role: p.role || "viewer",
            expiresAt: p.expiresAt ?? null,
            ...(typeof p.allowDownload === "boolean"
              ? { allowDownload: p.allowDownload }
              : {}),
            ...(typeof p.watermark === "boolean" ? { watermark: p.watermark } : {}),
          }))
        : undefined;

    const cleanedLink =
      link && typeof link === "object"
        ? {
            enabled: !!link.enabled,
            role: link.role || "viewer",
            requireLogin: link.requireLogin !== false,
            ...(typeof link.allowDownload === "boolean"
              ? { allowDownload: link.allowDownload }
              : {}),
            ...(typeof link.watermark === "boolean" ? { watermark: link.watermark } : {}),
            expiresAt: link.expiresAt === null ? null : link.expiresAt || undefined,
          }
        : undefined;

    return authJson("POST", "/share/put", {
      resourceId,
      ...(cleanedPeople ? { people: cleanedPeople } : {}),
      ...(cleanedLink ? { link: cleanedLink } : {}),
    });
  },

  
  async shareList(arg) {
    let resourceId = arg?.resourceId;
    if (!resourceId) {
      const { nodeId, kind = "file" } = arg || {};
      resourceId = buildResourceId(kind, nodeId);
    }
    const q = `?resourceId=${encodeURIComponent(resourceId)}`;
    return authJson("GET", `/share/list${q}`);
  },

 
  async shareRevoke(arg) {
    const resourceId =
      arg?.resourceId || buildResourceId(arg?.kind || "file", arg?.nodeId);
    const body = {
      resourceId,
      ...(arg?.email ? { email: String(arg.email).trim().toLowerCase() } : {}),
      ...(arg?.linkAll ? { linkAll: true } : {}),
    };
    return authJson("POST", "/share/revoke", body);
  },


  async getShareSummary({ nodeId, kind = "file", owner } = {}) {
    let q = `?nodeId=${encodeURIComponent(nodeId)}&kind=${encodeURIComponent(kind)}`;
    q = withOwnerQuery(q, owner);
    try {
      // Preferred (if backend implements it)
      return await authJson("GET", `/share/summary${q}`);
    } catch (err) {
      if ([404, 405, 501].includes(err?.status)) {
        // Fallback: derive a summary from the full ACL
        const resourceId = buildResourceId(kind, nodeId);
        const aclResp = await authJson(
          "GET",
          `/share/list?resourceId=${encodeURIComponent(resourceId)}`
        );

        const acl = aclResp?.acl || {};

        
        const principals = acl.principals || {};
        const legacy = Array.isArray(acl.principalEmails) ? acl.principalEmails : [];

        
        const normalizedPrincipals = { ...principals };
        for (const email of legacy) {
          if (!email) continue;
          const key = email.startsWith("user:")
            ? email
            : `user:${String(email).toLowerCase()}`;
          if (!normalizedPrincipals[key]) {
            normalizedPrincipals[key] = { role: "viewer" };
          }
        }

        const summary = {
          principals: normalizedPrincipals,
          link: acl.link || {},
          shared:
            !!acl.link?.enabled || Object.keys(normalizedPrincipals).length > 0,
        };

        // Return both shapes so your component can use res.summary || res.acl
        return { summary, acl };
      }
      throw err;
    }
  },

  // low-level helper for the PUT step
  uploadToSignedUrl,
};
